// GA4 Analytics Tracking Utilities

declare global {
 interface Window {
 gtag?: (...args: any[]) => void;
 twq?: (...args: any[]) => void;
 }
}

/**
 * Track X (Twitter) Ads conversion event
 */
export const trackXConversion = (
 eventId: string,
 params?: Record<string, any>
) => {
 if (typeof window !== 'undefined' && typeof window.twq === 'function') {
 window.twq('event', eventId, params || {});
 }
};

/**
 * Track a custom GA4 event
 */
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
 if (typeof window !== 'undefined' && window.gtag) {
 window.gtag('event', eventName, params);
 }
};

/**
 * Track demo CTA clicks
 */
export const trackDemoClick = (location: string) => {
 trackEvent('demo_cta_click', { location });
};

/**
 * Track cost savings/fees CTA clicks
 */
export const trackCostSavingsClick = (location: string) => {
 trackEvent('cost_savings_click', { location });
};

/**
 * Track FAQ expand events
 */
export const trackFaqExpand = (questionIndex: number, questionText: string) => {
 trackEvent('faq_expand', {
 question_index: questionIndex,
 question_text: questionText.substring(0, 100),
 });
};

/**
 * Track form started events
 */
export const trackFormStarted = (formType: string) => {
 trackEvent('form_started', { form_type: formType });
};

/**
 * Track form submitted events
 */
export const trackFormSubmitted = (formType: string) => {
 trackEvent('form_submitted', { form_type: formType });
};

/**
 * Initialize scroll depth tracking (50%)
 */
export const initScrollTracking = () => {
 let scrolled50 = false;

 const handleScroll = () => {
 if (scrolled50) return;

 const scrollPercent =
 (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;

 if (scrollPercent >= 50) {
 scrolled50 = true;
 trackEvent('scroll_50', { page_path: window.location.pathname });
 window.removeEventListener('scroll', handleScroll);
 }
 };

 window.addEventListener('scroll', handleScroll, { passive: true });

 return () => window.removeEventListener('scroll', handleScroll);
};

/**
 * Initialize time on page tracking (20 seconds)
 */
export const initTimeOnPageTracking = () => {
 const timer = setTimeout( () => {
 trackEvent('time_on_page_20s', { page_path: window.location.pathname });
 }, 20000);

 return () => clearTimeout(timer);
};

/**
 * Track CTA viewport visibility using Intersection Observer
 */
/**
 * Track signup funnel: started
 */
export const trackSignupStarted = (ctaLabel: string) => {
 trackEvent('signup_started', {
 cta_label: ctaLabel,
 page_path: typeof window !== 'undefined' ? window.location.pathname : '',
 });
};

/**
 * Track signup funnel: step 2 (redirect)
 */
export const trackSignupStep2Reached = () => {
 trackEvent('signup_step_2_reached');
};

/**
 * Track signup funnel: completed
 */
export const trackSignupCompleted = () => {
 trackEvent('signup_completed');
};

/**
 * Track early access CTA clicks
 */
export const trackEarlyAccessClick = (location: string) => {
 trackEvent('early_access_click', { location });
};

/**
 * Track CTA button clicks with text and placement
 */
export const trackCtaClick = (ctaText: string, placement: string) => {
 trackEvent('cta_click', {
 cta_text: ctaText,
 placement,
 page_path: typeof window !== 'undefined' ? window.location.pathname : '',
 });
};

/**
 * Track GA4 recommended generate_lead event
 */
export const trackGenerateLead = (source: string, email?: string | null) => {
 trackEvent('generate_lead', { source });
 // Google Ads — "Submit lead form" conversion
 trackEvent('conversion', {
 send_to: 'AW-18176803497/0J2FCIamm7AcEKmFsNtD',
 value: 1.0,
 currency: 'EUR',
 source,
 });
 // X (Twitter) Ads — Lead conversion event
 trackXConversion('tw-rcoky-rcoll', {
 email_address: email ?? null,
 });
};


/**
 * Track "how it works" link clicks
 */
export const trackHowItWorksClick = (location: string) => {
 trackEvent('how_it_works_click', { location });
};

/**
 * Track chat widget opened
 */
export const trackChatOpened = () => {
 trackEvent('chat_opened');
};

/**
 * Track chat message sent
 */
export const trackChatMessageSent = () => {
 trackEvent('chat_message_sent');
};

/**
 * Track chat lead captured
 */
export const trackChatLeadCaptured = (useCase: string, intentLevel: string) => {
 trackEvent('chat_lead_captured', { use_case: useCase, intent_level: intentLevel });
};

/**
 * Track chat demo click
 */
export const trackChatDemoClick = () => {
 trackEvent('chat_demo_click');
};

/**
 * Track CTA viewport visibility using Intersection Observer
 */
export const initCtaViewportTracking = (
 element: HTMLElement | null,
 ctaType: string
) => {
 if (!element || typeof IntersectionObserver === 'undefined') return () => {};

 let hasTracked = false;

 const observer = new IntersectionObserver(
 (entries) => {
 entries.forEach((entry) => {
      if (entry.isIntersecting && !hasTracked) {
 hasTracked = true;
 trackEvent('cta_viewed', { cta_type: ctaType });
 observer.disconnect();
 }
 });
 },
 { threshold: 0.5 }
 );

 observer.observe(element);

 return () => observer.disconnect();
};

/**
 * Track hero demo CTA click
 */
export const trackHeroDemoClick = () => {
 trackEvent('hero_demo_cta_click', {
 page_path: typeof window !== 'undefined' ? window.location.pathname : '',
 });
};

/**
 * Track mid-page demo CTA click
 */
export const trackMidPageDemoClick = () => {
 trackEvent('mid_page_demo_cta_click', {
 page_path: typeof window !== 'undefined' ? window.location.pathname : '',
 });
};

/**
 * Track footer demo link click
 */
export const trackFooterDemoClick = () => {
 trackEvent('footer_demo_cta_click', {
 page_path: typeof window !== 'undefined' ? window.location.pathname : '',
 });
};

/**
 * Track Calendly booking confirmation (key event)
 */
export const trackDemoBooked = () => {
 trackEvent('demo_booked', {
 page_path: typeof window !== 'undefined' ? window.location.pathname : '',
 });
};

/**
 * Hook: listen for Calendly postMessage events and fire demo_booked
 * Usage: useCalendlyListener in any component that embeds Calendly
 */
export const useCalendlyListener = () => {
 if (typeof window === 'undefined') return;

 const handler = (e: MessageEvent) => {
 if (e.data?.event === 'calendly.event_scheduled') {
 trackDemoBooked();
 }
 };

 window.addEventListener('message', handler);
 return () => window.removeEventListener('message', handler);
};
